import { obterUrlTile } from '@/services/geo-engine/requests';
/** Normaliza `tipo_geometria` (caixa, sufixos Z/M) e o reduz a uma das três famílias. */
export function familiaDaGeometria(tipo) {
    const normalizado = tipo
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '')
        .replace(/(ZM|Z|M)$/, '');
    switch (normalizado) {
        case 'POINT':
        case 'MULTIPOINT':
            return 'ponto';
        case 'LINESTRING':
        case 'MULTILINESTRING':
            return 'linha';
        case 'POLYGON':
        case 'MULTIPOLYGON':
            return 'poligono';
        default:
            return null;
    }
}
export const COR_POR_FAMILIA = {
    ponto: '#9c3f6a',
    linha: '#1f7a8c',
    poligono: '#2b6ea8',
};
export const COR_DA_SELECAO = '#0d3f66';
const OPACIDADE_DO_PREENCHIMENTO = 0.16;
/** Estilo inicial de uma camada, derivado somente da família geométrica. */
export function estiloPadrao(familia) {
    const cor = COR_POR_FAMILIA[familia];
    if (familia === 'poligono') {
        return {
            familia,
            cor,
            opacidadePreenchimento: OPACIDADE_DO_PREENCHIMENTO,
            corContorno: cor,
            larguraContorno: 1.2,
            opacidadeContorno: 1,
        };
    }
    if (familia === 'linha') {
        return { familia, cor, largura: 1.8, opacidade: 1 };
    }
    return { familia, cor, raio: 4, opacidade: 1, larguraContorno: 1 };
}
const PREFIXO = 'camada:';
export function idDaFonte(camadaId) {
    return `${PREFIXO}${camadaId}`;
}
/** Devolve o id da camada da Geo-Engine a partir do id de uma fonte do MapLibre, se for uma. */
export function camadaDaFonte(idDeFonte) {
    return idDeFonte.startsWith(PREFIXO) ? idDeFonte.slice(PREFIXO.length) : null;
}
export function idDoPreenchimento(camadaId) {
    return `${idDaFonte(camadaId)}:preenchimento`;
}
export function idDoContorno(camadaId) {
    return `${idDaFonte(camadaId)}:contorno`;
}
export function idDoPonto(camadaId) {
    return `${idDaFonte(camadaId)}:ponto`;
}
export function idDoRotuloDaCamada(camadaId) {
    return `${idDaFonte(camadaId)}:rotulo`;
}
/** Todos os ids de layer que uma camada pode ter no mapa, qualquer que seja a sua família. */
export function idsDeLayersDaCamada(camadaId) {
    return [
        idDoPreenchimento(camadaId),
        idDoContorno(camadaId),
        idDoPonto(camadaId),
        idDoRotuloDaCamada(camadaId),
    ];
}
/**
 * Filtro por id das feições da camada (`todos` => sem filtro; `ids` vazio => nenhuma feição).
 * Compartilhado pelas layers geométricas e pela layer de rótulos.
 */
export function filtroDeSelecaoDaCamada(selecao) {
    if (selecao.todos) {
        return null;
    }
    return selecao.ids.length === 0
        ? ['==', ['get', 'id'], -1]
        : ['in', ['get', 'id'], ['literal', [...selecao.ids]]];
}
/**
 * Filtro da layer de rótulos: descarta valores nulos/vazios do atributo nominal, aplica o filtro
 * de seleção da camada e, opcionalmente, exclui a feição que já tem rótulo individual.
 */
export function filtroDoRotuloDaCamada(atributo, filtroDeSelecao, idExcluido) {
    const condicoes = [
        ['!=', ['coalesce', ['to-string', ['get', atributo]], ''], ''],
    ];
    if (filtroDeSelecao !== null) {
        condicoes.push(filtroDeSelecao);
    }
    if (idExcluido !== null) {
        condicoes.push(['!=', ['get', 'id'], idExcluido]);
    }
    return ['all', ...condicoes];
}
/** Fonte vetorial MVT comum: `/camadas/{id}/tiles/{z}/{x}/{y}.mvt`. */
export function especificacaoDaFonte(camadaId) {
    return {
        type: 'vector',
        tiles: [obterUrlTile(camadaId, '{z}', '{x}', '{y}')],
    };
}
/** Layers da camada. O nome da source-layer do tile é o próprio id público da camada. */
export function especificacoesDasLayers(camadaId, familia, estiloRecebido) {
    const estilo = estiloRecebido ?? estiloPadrao(familia);
    const base = { source: idDaFonte(camadaId), 'source-layer': camadaId };
    if (familia === 'poligono' && estilo.familia === 'poligono') {
        const preenchimento = {
            id: idDoPreenchimento(camadaId),
            type: 'fill',
            ...base,
            paint: { 'fill-color': estilo.cor, 'fill-opacity': estilo.opacidadePreenchimento },
        };
        const contorno = {
            id: idDoContorno(camadaId),
            type: 'line',
            ...base,
            layout: { 'line-join': 'round' },
            paint: {
                'line-color': estilo.corContorno,
                'line-width': estilo.larguraContorno,
                'line-opacity': estilo.opacidadeContorno,
            },
        };
        return [preenchimento, contorno];
    }
    if (familia === 'linha' && estilo.familia === 'linha') {
        const linha = {
            id: idDoContorno(camadaId),
            type: 'line',
            ...base,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': estilo.cor, 'line-width': estilo.largura, 'line-opacity': estilo.opacidade },
        };
        return [linha];
    }
    if (estilo.familia === 'ponto') {
        const ponto = {
            id: idDoPonto(camadaId),
            type: 'circle',
            ...base,
            paint: {
                'circle-color': estilo.cor,
                'circle-radius': estilo.raio,
                'circle-opacity': estilo.opacidade,
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': estilo.larguraContorno,
            },
        };
        return [ponto];
    }
    return [];
}
const PAINT_DO_ROTULO_DA_CAMADA = {
    'text-color': '#123f2b',
    'text-halo-color': '#ffffff',
    'text-halo-width': 1.5,
    'text-halo-blur': 0.5,
};
const FONTES_DO_ROTULO_DA_CAMADA = ['Open Sans Regular', 'Arial Unicode MS Regular'];
/**
 * Rótulos automáticos de uma camada MVT: layer `symbol` sobre a mesma fonte vetorial. Posição,
 * colisão e repetição ficam a cargo do MapLibre; o atributo entra só como argumento de expressão.
 */
export function especificacaoDoRotuloDaCamada(camadaId, familia, atributo, filtro, visivel = true) {
    const base = {
        id: idDoRotuloDaCamada(camadaId),
        type: 'symbol',
        source: idDaFonte(camadaId),
        'source-layer': camadaId,
        ...(filtro === undefined ? {} : { filter: filtro }),
        paint: PAINT_DO_ROTULO_DA_CAMADA,
    };
    const comum = {
        'text-field': ['coalesce', ['to-string', ['get', atributo]], ''],
        'text-font': FONTES_DO_ROTULO_DA_CAMADA,
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        visibility: visivel ? 'visible' : 'none',
    };
    if (familia === 'poligono') {
        return {
            ...base,
            minzoom: 4,
            layout: {
                ...comum,
                'symbol-placement': 'point',
                'text-anchor': 'center',
                'text-size': 12,
                'text-max-width': 10,
                'text-padding': 8,
            },
        };
    }
    if (familia === 'linha') {
        return {
            ...base,
            minzoom: 7,
            layout: {
                ...comum,
                'symbol-placement': 'line',
                'text-size': 11,
                'symbol-spacing': 450,
                'text-padding': 10,
                'text-rotation-alignment': 'map',
                'text-keep-upright': true,
            },
        };
    }
    return {
        ...base,
        minzoom: 9,
        layout: {
            ...comum,
            'symbol-placement': 'point',
            'text-size': 11,
            'text-anchor': 'top',
            'text-offset': [0, 1.2],
            'text-padding': 6,
        },
    };
}
/** Aplica um estilo já em vigor diretamente nas layers existentes, sem recriá-las. */
export function aplicarEstiloNaCamada(mapa, camadaId, estilo) {
    const definir = (layerId, nome, valor) => {
        if (mapa.getLayer(layerId) !== undefined) {
            mapa.setPaintProperty(layerId, nome, valor);
        }
    };
    if (estilo.familia === 'poligono') {
        definir(idDoPreenchimento(camadaId), 'fill-color', estilo.cor);
        definir(idDoPreenchimento(camadaId), 'fill-opacity', estilo.opacidadePreenchimento);
        definir(idDoContorno(camadaId), 'line-color', estilo.corContorno);
        definir(idDoContorno(camadaId), 'line-width', estilo.larguraContorno);
        definir(idDoContorno(camadaId), 'line-opacity', estilo.opacidadeContorno);
        return;
    }
    if (estilo.familia === 'linha') {
        definir(idDoContorno(camadaId), 'line-color', estilo.cor);
        definir(idDoContorno(camadaId), 'line-width', estilo.largura);
        definir(idDoContorno(camadaId), 'line-opacity', estilo.opacidade);
        return;
    }
    definir(idDoPonto(camadaId), 'circle-color', estilo.cor);
    definir(idDoPonto(camadaId), 'circle-radius', estilo.raio);
    definir(idDoPonto(camadaId), 'circle-opacity', estilo.opacidade);
    definir(idDoPonto(camadaId), 'circle-stroke-width', estilo.larguraContorno);
}
// --- Realce da feição selecionada --------------------------------------------------------
export const ID_DA_FONTE_DE_SELECAO = 'selecao';
export const ID_DO_PREENCHIMENTO_DE_SELECAO = 'selecao:preenchimento';
export const ID_DO_CONTORNO_DE_SELECAO = 'selecao:contorno';
export const ID_DO_PONTO_DE_SELECAO = 'selecao:ponto';
export const ID_DA_FONTE_DE_ROTULO_DA_SELECAO = 'selecao:fonte-rotulo';
export const ID_DA_LAYER_DE_ROTULO_DA_SELECAO = 'selecao:rotulo-unico';
/** Resultado de uma operação espacial (buffer) desenhado sobre o mapa. */
export const ID_DA_FONTE_DE_OPERACAO = 'operacao';
export const ID_DO_PREENCHIMENTO_DE_OPERACAO = 'operacao:preenchimento';
export const ID_DO_CONTORNO_DE_OPERACAO = 'operacao:contorno';
export const ID_DO_PONTO_DE_OPERACAO = 'operacao:ponto';
export const COR_DA_OPERACAO = '#a9741f';
function layersDeGeoJson(fonte, ids, cor, opcoes) {
    const preenchimento = {
        id: ids.preenchimento,
        type: 'fill',
        source: fonte,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: { 'fill-color': cor, 'fill-opacity': opcoes.preenchimento },
    };
    const contorno = {
        id: ids.contorno,
        type: 'line',
        source: fonte,
        filter: ['!=', ['geometry-type'], 'Point'],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
            'line-color': cor,
            'line-width': 2.6,
            ...(opcoes.tracejado ? { 'line-dasharray': [3, 2] } : {}),
        },
    };
    const ponto = {
        id: ids.ponto,
        type: 'circle',
        source: fonte,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
            'circle-color': cor,
            'circle-radius': 7,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
        },
    };
    return [preenchimento, contorno, ponto];
}
/**
 * Rótulo da feição selecionada: uma única layer de texto sobre uma fonte própria, que contém
 * somente um ponto (`rotulo`). Evita a repetição do texto por tile/parte da geometria.
 */
export function especificacaoDoRotuloDaSelecao() {
    return {
        id: ID_DA_LAYER_DE_ROTULO_DA_SELECAO,
        type: 'symbol',
        source: ID_DA_FONTE_DE_ROTULO_DA_SELECAO,
        layout: {
            'text-field': ['get', 'rotulo'],
            'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
            'text-size': 14,
            'text-anchor': 'center',
            'text-max-width': 12,
            'text-allow-overlap': false,
            'text-ignore-placement': false,
        },
        paint: {
            'text-color': '#123f2b',
            'text-halo-color': '#ffffff',
            'text-halo-width': 2,
            'text-halo-blur': 0.5,
        },
    };
}
export function especificacoesDaSelecao() {
    return layersDeGeoJson(ID_DA_FONTE_DE_SELECAO, {
        preenchimento: ID_DO_PREENCHIMENTO_DE_SELECAO,
        contorno: ID_DO_CONTORNO_DE_SELECAO,
        ponto: ID_DO_PONTO_DE_SELECAO,
    }, COR_DA_SELECAO, { preenchimento: 0.12, tracejado: false });
}
export function especificacoesDaOperacao() {
    return layersDeGeoJson(ID_DA_FONTE_DE_OPERACAO, {
        preenchimento: ID_DO_PREENCHIMENTO_DE_OPERACAO,
        contorno: ID_DO_CONTORNO_DE_OPERACAO,
        ponto: ID_DO_PONTO_DE_OPERACAO,
    }, COR_DA_OPERACAO, { preenchimento: 0.07, tracejado: true });
}
// --- Relevo ----------------------------------------------------------------------------
/**
 * Relevo sombreado (hillshade), leitura física de base. Fonte pública, sem chave/conta:
 * elevation-tiles-prod, mantida pela AWS Open Data (SRTM, GMTED2010, ETOPO1), cobertura global.
 * https://registry.opendata.aws/terrain-tiles/
 */
export const ID_DA_FONTE_DE_RELEVO = 'fonte-relevo';
export const ID_DA_CAMADA_DE_RELEVO = 'camada-relevo';
export const ID_DA_CAMADA_HIPSOMETRICA = 'camada-hipsometrica';
/**
 * Paradas hipsométricas: elevação em metros e cor. Única fonte da escala, compartilhada pela
 * layer `color-relief` e pela legenda.
 */
export const ESCALA_HIPSOMETRICA = [
    [-11000, '#173b54'],
    [-6000, '#285c76'],
    [-3000, '#43839a'],
    [-1000, '#78adbb'],
    [-200, '#b3d4d5'],
    [-1, '#d9e8e4'],
    [0, '#76a86a'],
    [100, '#8db86e'],
    [250, '#a6c47a'],
    [500, '#d2c781'],
    [800, '#d8b477'],
    [1200, '#c39469'],
    [1800, '#a77c64'],
    [2500, '#958074'],
    [3500, '#c0aa96'],
    [5000, '#e3d8c9'],
    [6500, '#f7f5ef'],
];
export const URL_DA_FONTE_DE_RELEVO = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';
export const ATRIBUICAO_DO_RELEVO = 'Relevo: dados de elevação públicos (SRTM/GMTED2010/ETOPO1), via AWS Terrain Tiles';
export function especificacaoDoRelevo(exaggeration = 0.55) {
    return {
        id: ID_DA_CAMADA_DE_RELEVO,
        type: 'hillshade',
        source: ID_DA_FONTE_DE_RELEVO,
        paint: {
            'hillshade-exaggeration': exaggeration,
            'hillshade-shadow-color': '#35493c',
            'hillshade-highlight-color': '#fff8e9',
            'hillshade-accent-color': '#526c58',
        },
    };
}
/**
 * Hipsometria: cor por altitude (metros) calculada pelo MapLibre a partir da mesma fonte DEM
 * do hillshade. Escala contínua; não representa classes temáticas nem cobertura da superfície.
 */
export function especificacaoDaHipsometria() {
    return {
        id: ID_DA_CAMADA_HIPSOMETRICA,
        type: 'color-relief',
        source: ID_DA_FONTE_DE_RELEVO,
        paint: {
            'color-relief-opacity': 1,
            'color-relief-color': [
                'interpolate',
                ['linear'],
                ['elevation'],
                ...ESCALA_HIPSOMETRICA.flatMap(([metros, cor]) => [metros, cor]),
            ],
        },
    };
}
